// Create a new file: src/views/Orders/OrderList/ProductSummaryPrintModal.tsx
import React, { useState, useRef } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from "reactstrap";
import "../index.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faPrint, faFileExcel } from "@fortawesome/free-solid-svg-icons";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import * as XLSX from "xlsx";
import { Order } from "..";


interface ProductSummaryPrintModalProps {
  showModal: boolean;
  handleModalClose: () => void;
  orderList: Order[];
  cartName: string;
  orderDate: string;
  isAllCartsSelected: boolean;
  menuItemCounts: {
    name: string;
    totalCount: number;
    reservedCount: number;
  }[];
}

const ProductSummaryPrintModal: React.FC<ProductSummaryPrintModalProps> = ({
  showModal,
  handleModalClose,
  orderList,
  cartName,
  orderDate,
  isAllCartsSelected,
  menuItemCounts,
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);
  const isDownloadingRef = useRef(false);
  const isDownloadingExcelRef = useRef(false);

  // Create cart-wise summary data for printing
  const getCartWiseSummaryForPrint = () => {
    if (!isAllCartsSelected) return [];

    const cartWiseData: Record<string, {
      cartName: string;
      cartId: string;
      items: Record<string, {
        name: string;
        totalCount: number;
        reservedCount: number;
      }>;
    }> = {};

    orderList.forEach((order) => {
      if (order.orderStatus === "Cancelled" || order.orderStatus === "cancelled") {
        return;
      }

      const cartName = order.cart?.displayName || "Direct Restaurant Orders";
      const cartId = order.cart?.cartId || "direct-restaurant";
      const hasCustomer = !!order.customer;

      if (!cartWiseData[cartId]) {
        cartWiseData[cartId] = {
          cartName,
          cartId,
          items: {},
        };
      }

      order.orderItems.forEach((item) => {
        if (!cartWiseData[cartId].items[item.menuItemId]) {
          cartWiseData[cartId].items[item.menuItemId] = {
            name: item.displayName,
            totalCount: 0,
            reservedCount: 0,
          };
        }

        cartWiseData[cartId].items[item.menuItemId].totalCount += item.quantity;

        if (hasCustomer) {
          cartWiseData[cartId].items[item.menuItemId].reservedCount += item.quantity;
        }
      });
    });

    return Object.values(cartWiseData).map(cart => ({
      ...cart,
      items: Object.values(cart.items).sort((a, b) => b.totalCount - a.totalCount)
    })).sort((a, b) => a.cartName.localeCompare(b.cartName));
  };

  const handlePrint = () => {
    const printWindow = window.open('', '', 'height=600,width=800');
    if (!printWindow) return;

    let printContent = `
      <html>
        <head>
          <title>Product Summary Print</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 30px; }
            .cart-section { margin-bottom: 40px; page-break-inside: avoid; }
            .cart-title { 
              font-size: 18px; 
              font-weight: bold; 
              color: #0066cc; 
              margin-bottom: 15px; 
              border-bottom: 2px solid #0066cc;
              padding-bottom: 5px;
            }
            table { 
              width: 100%; 
              border-collapse: collapse; 
              margin-bottom: 20px;
            }
            th, td { 
              border: 1px solid #ddd; 
              padding: 8px; 
              text-align: left; 
            }
            th { 
              background-color: #f2f2f2; 
              font-weight: bold; 
            }
            .total-row { 
              background-color: #f9f9f9; 
              font-weight: bold; 
            }
            .page-break { 
              page-break-before: always; 
            }
            @media print {
              .cart-section { 
                page-break-inside: avoid; 
              }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h2>Product Summary Report</h2>
            <p>Cart: ${cartName} | Date: ${orderDate}</p>
          </div>
    `;

    if (isAllCartsSelected) {
      // Cart-wise summary tables
      const cartWiseSummary = getCartWiseSummaryForPrint();
      cartWiseSummary.forEach((cartData: any, index: number) => {
        printContent += `
          <div class="cart-section ${index > 0 ? 'page-break' : ''}">
            <h3 class="cart-title">${cartData.cartName}</h3>
            <table>
              <thead>
                <tr>
                  <th>Menu Item</th>
                  <th>Total Quantity</th>
                  <th>Reserved Items</th>
                  <th>Available Quantity</th>
                </tr>
              </thead>
              <tbody>
        `;

        let totalQuantity = 0;
        let totalReserved = 0;

        cartData.items.forEach((item: any) => {
          totalQuantity += item.totalCount;
          totalReserved += item.reservedCount;
          
          printContent += `
            <tr>
              <td>${item.name}</td>
              <td>${item.totalCount}</td>
              <td>${item.reservedCount}</td>
              <td>${item.totalCount - item.reservedCount}</td>
            </tr>
          `;
        });

        printContent += `
                <tr class="total-row">
                  <td><strong>Total</strong></td>
                  <td><strong>${totalQuantity}</strong></td>
                  <td><strong>${totalReserved}</strong></td>
                  <td><strong>${totalQuantity - totalReserved}</strong></td>
                </tr>
              </tbody>
            </table>
          </div>
        `;
      });
    } else {
      // Single cart summary
      printContent += `
        <div class="cart-section">
          <table>
            <thead>
              <tr>
                <th>Menu Item</th>
                <th>Total Quantity</th>
                <th>Reserved Items</th>
                <th>Available Quantity</th>
              </tr>
            </thead>
            <tbody>
      `;

      let totalQuantity = 0;
      let totalReserved = 0;

      menuItemCounts.forEach((item: any) => {
        totalQuantity += item.totalCount;
        totalReserved += item.reservedCount;
        
        printContent += `
          <tr>
            <td>${item.name}</td>
            <td>${item.totalCount}</td>
            <td>${item.reservedCount}</td>
            <td>${item.totalCount - item.reservedCount}</td>
          </tr>
        `;
      });

      printContent += `
              <tr class="total-row">
                <td><strong>Total</strong></td>
                <td><strong>${totalQuantity}</strong></td>
                <td><strong>${totalReserved}</strong></td>
                <td><strong>${totalQuantity - totalReserved}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
      `;
    }

    printContent += `
        </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
    
    handleModalClose();
  };

  const handleDownloadPdf = async () => {
    if (isDownloadingRef.current) return;
    isDownloadingRef.current = true;
    setIsDownloading(true);

    try {
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();

      if (isAllCartsSelected) {
        const cartWiseSummary = getCartWiseSummaryForPrint();
        
        for (let cartIndex = 0; cartIndex < cartWiseSummary.length; cartIndex++) {
          const cartData = cartWiseSummary[cartIndex];
          
          const tempDiv = document.createElement("div");
          tempDiv.style.position = "absolute";
          tempDiv.style.left = "-9999px";
          tempDiv.style.width = "210mm";
          tempDiv.style.padding = "20px";
          tempDiv.style.backgroundColor = "white";

          tempDiv.innerHTML = `
            <div style="text-align: center; margin-bottom: 20px; font-size: 18px; font-weight: bold;">
              Product Summary for ${cartData.cartName} on ${orderDate}
            </div>
            <table style="width: 100%; border-collapse: collapse; font-size: 12px;">
              <thead>
                <tr style="background-color: #f8f9fa;">
                  <th style="border: 1px solid #dee2e6; padding: 8px; text-align: left;">#</th>
                  <th style="border: 1px solid #dee2e6; padding: 8px; text-align: left;">Menu Item</th>
                  <th style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">Total Quantity</th>
                  <th style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">Reserved Items</th>
                  <th style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">Available Quantity</th>
                </tr>
              </thead>
              <tbody>
                ${cartData.items.map((item: any, index: number) => `
                  <tr>
                    <td style="border: 1px solid #dee2e6; padding: 8px;">${index + 1}</td>
                    <td style="border: 1px solid #dee2e6; padding: 8px;">${item.name}</td>
                    <td style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">${item.totalCount}</td>
                    <td style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">${item.reservedCount}</td>
                    <td style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">${item.totalCount - item.reservedCount}</td>
                  </tr>
                `).join("")}
                <tr style="font-weight: bold;">
                  <td colspan="2" style="border: 1px solid #dee2e6; padding: 8px; text-align: right;">Total:</td>
                  <td style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">
                    ${cartData.items.reduce((sum: number, item: any) => sum + item.totalCount, 0)}
                  </td>
                  <td style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">
                    ${cartData.items.reduce((sum: number, item: any) => sum + item.reservedCount, 0)}
                  </td>
                  <td style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">
                    ${cartData.items.reduce((sum: number, item: any) => sum + (item.totalCount - item.reservedCount), 0)}
                  </td>
                </tr>
              </tbody>
            </table>
          `;

          document.body.appendChild(tempDiv);

          const canvas = await html2canvas(tempDiv, {
            scale: 2,
            useCORS: true,
            allowTaint: true,
          });

          const imgData = canvas.toDataURL("image/png");
          const imgProps = pdf.getImageProperties(imgData);
          const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

          if (cartIndex > 0) {
            pdf.addPage();
          }

          pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
          document.body.removeChild(tempDiv);
        }

        pdf.save(`All_Carts_Product_Summary_${orderDate}.pdf`);
      } else {
        // Single cart PDF generation
        const tempDiv = document.createElement("div");
        tempDiv.style.position = "absolute";
        tempDiv.style.left = "-9999px";
        tempDiv.style.width = "210mm";
        tempDiv.style.padding = "20px";
        tempDiv.style.backgroundColor = "white";

        tempDiv.innerHTML = `
          <div style="text-align: center; margin-bottom: 20px; font-size: 18px; font-weight: bold;">
            Product Summary for ${cartName} on ${orderDate}
          </div>
          <table style="width: 100%; border-collapse: collapse; font-size: 12px;">
            <thead>
              <tr style="background-color: #f8f9fa;">
                <th style="border: 1px solid #dee2e6; padding: 8px; text-align: left;">#</th>
                <th style="border: 1px solid #dee2e6; padding: 8px; text-align: left;">Menu Item</th>
                <th style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">Total Quantity</th>
                <th style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">Reserved Items</th>
                <th style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">Available Quantity</th>
              </tr>
            </thead>
            <tbody>
              ${menuItemCounts.map((item: any, index: number) => `
                <tr>
                  <td style="border: 1px solid #dee2e6; padding: 8px;">${index + 1}</td>
                  <td style="border: 1px solid #dee2e6; padding: 8px;">${item.name}</td>
                  <td style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">${item.totalCount}</td>
                  <td style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">${item.reservedCount}</td>
                  <td style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">${item.totalCount - item.reservedCount}</td>
                </tr>
              `).join("")}
              <tr style="font-weight: bold;">
                <td colspan="2" style="border: 1px solid #dee2e6; padding: 8px; text-align: right;">Total:</td>
                <td style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">
                  ${menuItemCounts.reduce((sum, item) => sum + item.totalCount, 0)}
                </td>
                <td style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">
                  ${menuItemCounts.reduce((sum, item) => sum + item.reservedCount, 0)}
                </td>
                <td style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">
                  ${menuItemCounts.reduce((sum, item) => sum + (item.totalCount - item.reservedCount), 0)}
                </td>
              </tr>
            </tbody>
          </table>
        `;

        document.body.appendChild(tempDiv);

        const canvas = await html2canvas(tempDiv, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
        });

        const imgData = canvas.toDataURL("image/png");
        const imgProps = pdf.getImageProperties(imgData);
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

        pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
        document.body.removeChild(tempDiv);

        pdf.save(`${cartName}_Product_Summary_${orderDate}.pdf`);
      }

      setIsDownloading(false);
      handleModalClose();
    } catch (error) {
      console.error("Error generating PDF:", error);
      setIsDownloading(false);
    } finally {
      isDownloadingRef.current = false;
    }
  };

  const handleDownloadExcel = () => {
    if (isDownloadingExcelRef.current) return;
    isDownloadingExcelRef.current = true;
    setIsDownloadingExcel(true);

    try {
      const wb = XLSX.utils.book_new();
      const headerRow = ["#", "Menu Item", "Total Quantity", "Reserved Items", "Available Quantity"];
      const colWidths = [{ wch: 5 }, { wch: 35 }, { wch: 16 }, { wch: 16 }, { wch: 20 }];

      if (isAllCartsSelected) {
        const cartWiseSummary = getCartWiseSummaryForPrint();

        cartWiseSummary.forEach((cartData: any) => {
          const dataRows = cartData.items.map((item: any, index: number) => [
            index + 1, item.name, item.totalCount, item.reservedCount,
            item.totalCount - item.reservedCount,
          ]);
          const totalRow = [
            "", "Total:",
            cartData.items.reduce((s: number, i: any) => s + i.totalCount, 0),
            cartData.items.reduce((s: number, i: any) => s + i.reservedCount, 0),
            cartData.items.reduce((s: number, i: any) => s + (i.totalCount - i.reservedCount), 0),
          ];
          const ws = XLSX.utils.aoa_to_sheet([headerRow, ...dataRows, totalRow]);
          ws["!cols"] = colWidths;
          XLSX.utils.book_append_sheet(wb, ws, cartData.cartName.slice(0, 31));
        });

        XLSX.writeFile(wb, `All_Carts_Product_Summary_${orderDate}.xlsx`);
      } else {
        const dataRows = menuItemCounts.map((item, index) => [
          index + 1, item.name, item.totalCount, item.reservedCount,
          item.totalCount - item.reservedCount,
        ]);
        const totalRow = [
          "", "Total:",
          menuItemCounts.reduce((s, i) => s + i.totalCount, 0),
          menuItemCounts.reduce((s, i) => s + i.reservedCount, 0),
          menuItemCounts.reduce((s, i) => s + (i.totalCount - i.reservedCount), 0),
        ];
        const ws = XLSX.utils.aoa_to_sheet([headerRow, ...dataRows, totalRow]);
        ws["!cols"] = colWidths;
        XLSX.utils.book_append_sheet(wb, ws, (cartName || "Summary").slice(0, 31));
        XLSX.writeFile(wb, `${cartName}_Product_Summary_${orderDate}.xlsx`);
      }

      handleModalClose();
    } catch (error) {
      console.error("Error generating Excel:", error);
    } finally {
      isDownloadingExcelRef.current = false;
      setIsDownloadingExcel(false);
    }
  };

  const cartWiseSummary = getCartWiseSummaryForPrint();

  return (
    <Modal isOpen={showModal} toggle={handleModalClose} size="lg" className="modal-95w" centered>
      <ModalHeader toggle={handleModalClose}>
        <div className="d-flex justify-content-between align-items-center w-100">
          <span>Product Summary Print Preview</span>
          <div className="d-flex align-items-center gap-3">
            <FontAwesomeIcon
              icon={faFileExcel}
              className={isDownloadingExcel ? "text-muted" : "text-success"}
              style={{
                cursor: isDownloadingExcel ? "not-allowed" : "pointer",
                fontSize: "1.2rem"
              }}
              onClick={!isDownloadingExcel ? handleDownloadExcel : undefined}
              title="Download Excel"
            />
            <FontAwesomeIcon
              icon={faDownload}
              className={isDownloading ? "text-muted" : "text-primary"}
              style={{
                cursor: isDownloading ? "not-allowed" : "pointer",
                fontSize: "1.2rem"
              }}
              onClick={!isDownloading ? handleDownloadPdf : undefined}
              title="Download PDF"
            />
          </div>
        </div>
      </ModalHeader>
      <ModalBody style={{ maxHeight: "70vh", overflowY: "auto" }}>
        <div className="text-center mb-4">
          <h5>Product Summary Report</h5>
          {/* <p>Cart: {cartName} | Date: {orderDate}</p>
          {isAllCartsSelected && (
            <p className="text-muted">
              This will print separate tables for each cart
            </p>
          )} */}
        </div>

        {isAllCartsSelected ? (
          // Cart-wise preview
          <>
            {cartWiseSummary.map((cartData: any, index: number) => (
              <div key={cartData.cartId} className="mb-4">
                <h6 className="text-primary mb-3">{cartData.cartName}</h6>
                <table className="table table-bordered table-sm">
                  <thead className="table-light">
                    <tr>
                      <th>#</th>
                      <th>Menu Item</th>
                      <th className="text-center">Total Quantity</th>
                      <th className="text-center">Reserved Items</th>
                      <th className="text-center">Available Quantity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cartData.items.map((item: any, itemIndex: number) => (
                      <tr key={itemIndex}>
                        <td>{itemIndex + 1}</td>
                        <td>{item.name}</td>
                        <td className="text-center">{item.totalCount}</td>
                        <td className="text-center">{item.reservedCount}</td>
                        <td className="text-center">{item.totalCount - item.reservedCount}</td>
                      </tr>
                    ))}
                    <tr className="fw-bold">
                      <td colSpan={2} className="text-end">Total:</td>
                      <td className="text-center">
                        {cartData.items.reduce((sum: number, item: any) => sum + item.totalCount, 0)}
                      </td>
                      <td className="text-center">
                        {cartData.items.reduce((sum: number, item: any) => sum + item.reservedCount, 0)}
                      </td>
                      <td className="text-center">
                        {cartData.items.reduce((sum: number, item: any) => sum + (item.totalCount - item.reservedCount), 0)}
                      </td>
                    </tr>
                  </tbody>
                </table>
                {index < cartWiseSummary.length - 1 && <hr />}
              </div>
            ))}
          </>
        ) : (
          // Single cart preview
          <table className="table table-bordered table-sm">
            <thead className="table-light">
              <tr>
                <th>#</th>
                <th>Menu Item</th>
                <th className="text-center">Total Quantity</th>
                <th className="text-center">Reserved Items</th>
                <th className="text-center">Available Quantity</th>
              </tr>
            </thead>
            <tbody>
              {menuItemCounts.map((item, index) => (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td>{item.name}</td>
                  <td className="text-center">{item.totalCount}</td>
                  <td className="text-center">{item.reservedCount}</td>
                  <td className="text-center">{item.totalCount - item.reservedCount}</td>
                </tr>
              ))}
              <tr className="fw-bold">
                <td colSpan={2} className="text-end">Total:</td>
                <td className="text-center">
                  {menuItemCounts.reduce((sum, item) => sum + item.totalCount, 0)}
                </td>
                <td className="text-center">
                  {menuItemCounts.reduce((sum, item) => sum + item.reservedCount, 0)}
                </td>
                <td className="text-center">
                  {menuItemCounts.reduce((sum, item) => sum + (item.totalCount - item.reservedCount), 0)}
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={handleModalClose}>
          Cancel
        </Button>
        <Button color="primary" onClick={handlePrint}>
          <FontAwesomeIcon icon={faPrint} className="me-2" />
          Print
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ProductSummaryPrintModal;